import { useEffect, useRef, useState } from "react";
import { api, type ReviewRubric, type ReviewRunStatus } from "../../api/client";
import { ApprovalCard } from "../ApprovalCard";
import { RubricCard } from "./RubricCard";

const POLL_MS = 2000;

// Live panel for a backend enhance-review run (fire-and-poll, the
// RemediationRunPanel pattern): status + the baseline → final rubric + held
// risky actions as ApprovalCards + the posted-comment link. Held approvals are
// HOST JsonStore rows (`appr-*`) — they resolve through the legacy host
// decision route, not the Convex approvals mutation.
export function ReviewRunPanel({
  runId,
  record,
  agentName,
  baseline,
  onSettled,
  pollMs = POLL_MS,
}: {
  runId?: string | null;
  // A PERSISTED run record (the Convex row) rendered statically — the
  // post-restart fallback when the host has no live run to poll. A record
  // whose state is still "running" means the host died mid-run: rendered as
  // "interrupted", and its held approvals are stale (not actionable).
  record?: ReviewRunStatus | null;
  agentName?: string;
  // The instant rubric shown while the run warms up (the status carries the
  // authoritative baseline once the first poll lands).
  baseline?: ReviewRubric | null;
  onSettled?: () => void;
  pollMs?: number;
}) {
  const [polled, setPolled] = useState<ReviewRunStatus | null>(null);
  const [expired, setExpired] = useState(false);
  const [notFound, setNotFound] = useState(false);
  // Approvals the user decided locally — hide their cards immediately (the
  // run record keeps the full audit trail).
  const [decided, setDecided] = useState<Record<string, "approved" | "denied">>({});
  const sawRunning = useRef(false);
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;
  const status = record ?? polled;

  useEffect(() => {
    if (record || !runId) return; // record mode is static — nothing to poll
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let inFlight = false;
    setPolled(null);
    setExpired(false);
    setNotFound(false);
    setDecided({});
    sawRunning.current = false;

    async function poll() {
      if (inFlight || cancelled) return;
      inFlight = true;
      try {
        const s = await api.getReviewRunStatus(runId as string);
        if (cancelled) return;
        setPolled(s);
        if (s.state === "running") sawRunning.current = true;
        if (s.state === "done" || s.state === "error") {
          onSettledRef.current?.();
          return; // terminal — stop
        }
      } catch (err) {
        if (cancelled) return;
        const code = (err as { status?: number })?.status;
        // ANY 404 is terminal: seen-running → the host evicted the finished
        // run (expired); otherwise unknown id. Never loop forever.
        if (code === 404) {
          if (sawRunning.current) setExpired(true);
          else setNotFound(true);
          onSettledRef.current?.();
          return;
        }
        // transient error → keep polling
      } finally {
        inFlight = false;
      }
      if (!cancelled) timer = setTimeout(poll, pollMs);
    }
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, pollMs, record]);

  const rubric = status?.baseline ?? baseline ?? null;
  // A record's held approvals are stale (the host that owned them is gone) —
  // never actionable in record mode.
  const held = record
    ? []
    : (status?.approvals || []).filter((a) => a.decision === "held" && !decided[a.id]);
  const interrupted = Boolean(record && record.state === "running");

  return (
    <div className="review-run-panel" data-testid="review-run-panel">
      {/* Rendered for EVERY state, not just `done`. An unverified record with
          state:"error" shows the attacker's string as "Review run failed: …"
          and one with state:"running" shows the interrupted note — both were
          unattributed lies about a teammate's PR, even though no link is
          rendered on those paths. */}
      {status?.unverified ? (
        <p className="review-run-note" data-testid="review-run-unverified">
          Recorded by another user{status.createdBy ? ` (${status.createdBy})` : ""} and not
          host-attested — treat this result as unverified.
        </p>
      ) : null}
      {notFound ? (
        <p className="review-run-error" data-testid="review-run-not-found">
          Run not found.
        </p>
      ) : expired ? (
        <p className="review-run-note">Run completed — live status expired.</p>
      ) : interrupted ? (
        <p className="review-run-note" data-testid="review-run-interrupted">
          This run was interrupted (the app closed mid-run). Re-run Fix to 100 to review again.
        </p>
      ) : !status || status.state === "running" ? (
        <div className="review-run-running" data-testid="review-run-running">
          <p className="review-run-note review-run-note--busy">
            <span className="review-run-spinner" data-testid="review-run-spinner" aria-hidden />
            {status?.phase === "setup"
              ? "Preparing the PR worktree…"
              : "Your agent is reviewing and fixing the PR…"}
          </p>
          {status?.textPreview ? (
            <p className="review-run-preview" data-testid="review-run-preview">
              {status.textPreview}
            </p>
          ) : null}
          {status?.activity && status.activity.length > 0 ? (
            <ul className="review-run-activity" data-testid="review-run-activity">
              {status.activity.slice(-4).map((a, i) => (
                <li key={`${a.ts}-${i}`} className="review-run-activity__item">
                  <span className="review-run-activity__label">{a.label}</span>
                  {a.detail ? (
                    <code className="review-run-activity__detail">{a.detail}</code>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
          {rubric ? <RubricCard rubric={rubric} /> : null}
        </div>
      ) : status.state === "error" ? (
        <p className="review-run-error" data-testid="review-run-error">
          Review run failed: {status.error || "unknown error"}
        </p>
      ) : (
        <div className="review-run-done" data-testid="review-run-done">
          <p className="review-run-title" data-testid="review-run-title">
            Review run finished — PR #{status.prNumber}
            {status.error
              ? ""
              : status.rubricFinal
                ? ` · score ${status.rubricFinal.overall} / 5`
                : ""}
          </p>
          {rubric ? <RubricCard rubric={rubric} final={status.rubricFinal ?? null} /> : null}
          {status.error ? (
            <p className="review-run-error" data-testid="review-run-send-error">
              Agent error: {status.error}
            </p>
          ) : null}
          {status.timedOut ? (
            <p className="review-run-note" data-testid="review-run-timeout-note">
              The agent hit the time limit — the score above reflects what was reached.
            </p>
          ) : null}
          {/* A run record this viewer neither created nor can attest to. The
              record is still shown — a teammate reviewing your PR is normal and
              hiding it would be its own failure — but it is attributed, and its
              caller-supplied commentUrl is NOT rendered as a link. Without
              this, a member could insert a run for someone else's PR with a
              clean rubric and an attacker-chosen "View the posted review
              comment" URL, indistinguishable from a real review. */}
          {status.commentUrl && !status.unverified ? (
            <p className="review-run-note">
              <a
                href={status.commentUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="review-run-comment-link"
              >
                View the posted review comment
              </a>
            </p>
          ) : status.commentUrl && status.unverified ? (
            <p className="review-run-note" data-testid="review-run-comment-unverified">
              A comment URL was recorded, but it came from an unverified run and is not linked.
            </p>
          ) : (
            <p className="review-run-note" data-testid="review-run-no-comment">
              No review comment was posted.
            </p>
          )}
          {status.pushed === false ? (
            <p className="review-run-note" data-testid="review-run-prepush-note">
              {held.length > 0
                ? "Fixes were not pushed (score is pre-push) — approve the push to apply them."
                : `Fixes were not pushed (score is pre-push) — they remain on the local run branch${
                    status.branch ? ` ${status.branch}` : ""
                  }. Re-run Fix to 100 to push and post the review comment.`}
            </p>
          ) : null}
        </div>
      )}
      {held.map((a) => (
        <ApprovalCard
          key={a.id}
          request={{
            id: a.id,
            agent: agentName || "agent",
            command: a.command,
            pack: a.pack,
            rule: a.rule,
            reason: a.reason,
          }}
          onDecision={async (decision) => {
            // Held review-run asks are host JsonStore rows — decide via the
            // legacy host route ("chat" scope has no meaning here; both
            // buttons resolve once).
            await api.decideHostApproval(a.id, decision);
            setDecided((d) => ({ ...d, [a.id]: decision }));
          }}
        />
      ))}
    </div>
  );
}
